import React, { useState, useEffect, useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Checkbox } from "../../ui/checkbox";
import { Card, CardContent, CardHeader } from "../../ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  MagnifyingGlass,
  Funnel,
  Check,
  CalendarBlank,
  Buildings,
  MapPin,
  GraduationCap,
  CaretLeft,
  CaretRight,
  Eye,
  Briefcase,
  CheckCircle,
  EnvelopeSimple,
  XCircle,
} from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useAcceptAllApplicationInformation,
  getVacancies,
  useBulkAcceptApplications,
} from "../../../adapters/Requests";
import ExportDropdown from "../../export";
import { DateRangeFilter } from "../../ui/daterangefilter";
import BulkAcceptModal from "../../modals/BulkAcceptModal";
import { Sheet, SheetContent } from "../../ui/sheet";
import AcceptanceDetailsViewer from "../../viewers/AcceptanceDetailsViewer";

export default function AllAcceptTable() {
  const navigate = useNavigate();
  const location = useLocation();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  const [selectedItType, setSelectedItType] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [uniqueItTypes, setUniqueItTypes] = useState([]);
  const [uniqueBatches, setUniqueBatches] = useState([]);
  const [uniqueLocations, setUniqueLocations] = useState([]);
  const [uniqueUnits, setUniqueUnits] = useState([]);

  const [rowSelection, setRowSelection] = useState({});
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);

  const bulkAcceptMutation = useBulkAcceptApplications();

  const { data, isLoading, isFetching, isError } = useAcceptAllApplicationInformation({
    limit: pagination.pageSize,
    page: pagination.pageIndex + 1,
    ittype: selectedItType,
    batch: selectedBatch,
    location: selectedLocation,
    businessUnit: selectedUnit,
    search: searchQuery,
    vetted_status: selectedStatus,
    start_date: dateRange.from,
    end_date: dateRange.to,
  });

  const { data: vacanciesData } = getVacancies();

  useEffect(() => {
    const searchParams = new URLSearchParams();
    if (pagination.pageIndex > 0)
      searchParams.set("page", pagination.pageIndex + 1);
    if (selectedItType) searchParams.set("ittype", selectedItType);
    if (selectedBatch) searchParams.set("batch", selectedBatch);
    if (selectedLocation) searchParams.set("location", selectedLocation);
    if (selectedUnit) searchParams.set("unit", selectedUnit);
    if (searchQuery) searchParams.set("search", searchQuery);
    if (dateRange.from) searchParams.set("from", dateRange.from);
    if (dateRange.to) searchParams.set("to", dateRange.to);

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  }, [
    pagination.pageIndex,
    selectedItType,
    selectedBatch,
    selectedLocation,
    selectedUnit,
    searchQuery,
    dateRange,
    navigate,
    location.pathname,
  ]);

  useEffect(() => {
    if (vacanciesData && vacanciesData.length > 0) {
      setUniqueItTypes(
        Array.from(
          new Set(
            vacanciesData.map((item) =>
              item.vacancy_position.name.toUpperCase(),
            ),
          ),
        ),
      );
      setUniqueBatches(
        Array.from(new Set(vacanciesData.map((item) => item.batch.name))),
      );
      setUniqueLocations(
        Array.from(new Set(vacanciesData.map((item) => item.location.name))),
      );
      setUniqueUnits(
        Array.from(
          new Set(vacanciesData.map((item) => item.business_unit.name)),
        ),
      );
    }
  }, [vacanciesData]);

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setSearchQuery(searchTerm);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };

  const hasSelections = Object.keys(rowSelection).length > 0;

  const handleRowClick = (row) => {
    if (hasSelections) {
      row.toggleSelected();
    } else {
      const data = row.original;
      setSelectedApplication({
        id: data.id,
        type: data.ittype.toLowerCase(),
      });
    }
  };

  const handleConfirmBulkAccept = async (departmentCode) => {
    const selectedRows = table.getSelectedRowModel().rows;
    const applicationIds = selectedRows.map((row) => row.original.id);

    try {
      const response = await bulkAcceptMutation.mutateAsync({
        application_ids: applicationIds,
        status: "ACCEPTED",
        department: departmentCode,
      });
      setBulkResult(response);
      setRowSelection({});
    } catch (error) {
      console.error("Bulk accept request failed:", error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACCEPTED":
        return "success";
      case "VERIFIED":
        return "warning";
      case "REJECTED":
        return "error";
      case "REVIEW":
        return "secondary";
      default:
        return "outline";
    }
  };

  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="mx-2"
        />
      ),
      cell: ({ row }) => (
        <div className="px-2" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      header: "Applicant",
      accessorKey: "firstName",
      cell: ({ row }) => (
        <div className="flex flex-col py-1">
          <span className="font-bold text-gray-900 text-sm">
            {row.getValue("firstName")}
          </span>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded text-gray-600 font-medium border border-gray-200">
              {row.original.age} yrs
            </span>
            <span className="text-[10px] text-gray-500">
              {row.original.state}
            </span>
          </div>
        </div>
      ),
    },
    {
      header: "Type",
      accessorKey: "ittype",
      cell: ({ row }) => (
        <Badge
          variant="outline"
          className="font-semibold text-[10px] px-2 bg-slate-50"
        >
          {row.getValue("ittype")}
        </Badge>
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }) => (
        <Badge
          variant={getStatusColor(row.getValue("status"))}
          className="font-semibold text-[10px] px-2"
        >
          {row.getValue("status")}
        </Badge>
      ),
    },
    {
      header: "Course",
      accessorKey: "cos",
      cell: ({ row }) => (
        <div
          className="flex items-center text-gray-700 text-xs font-medium max-w-[150px] truncate"
          title={row.getValue("cos")}
        >
          <GraduationCap className="mr-1.5 text-gray-400 shrink-0" size={14} />
          <span className="truncate">{row.getValue("cos")}</span>
        </div>
      ),
    },
    {
      header: "Placement",
      accessorKey: "businessunit",
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center text-gray-700 text-xs font-medium">
            <Briefcase className="mr-1.5 text-gray-400" size={14} />
            {row.getValue("businessunit")}
          </div>
          <div className="flex items-center text-gray-500 text-[11px]">
            <MapPin className="mr-1.5 text-gray-400" size={14} />
            {row.original.companylocation}
          </div>
        </div>
      ),
    },
    {
      header: "Date",
      accessorKey: "date",
      cell: ({ row }) => (
        <div className="flex items-center text-gray-500 text-xs">
          <CalendarBlank className="mr-1.5" size={14} />
          {row.getValue("date")}
        </div>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs gap-2 hover:bg-green-50 hover:text-green-700 hover:border-green-200"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedApplication({
              id: row.original.id,
              type: row.original.ittype.toLowerCase(),
            });
          }}
        >
          <Eye size={14} />
          View
        </Button>
      ),
    },
  ];

  const exportColumns = [
    { headerName: "ID", field: "code" },
    { headerName: "Name", field: "firstName" },
    { headerName: "Type", field: "ittype" },
    { headerName: "Status", field: "status" },
    { headerName: "Age", field: "age" },
    { headerName: "State", field: "state" },
    { headerName: "Course", field: "cos" },
    { headerName: "Batch", field: "batch" },
    { headerName: "Location", field: "companylocation" },
    { headerName: "Unit", field: "businessunit" },
    { headerName: "Date", field: "date" },
  ];

  const tableData = useMemo(() => {
    const rawData = Array.isArray(data) ? data : data?.applications || [];
    return rawData.map((app) => ({
      id: app.id,
      code: app.code,
      firstName: `${app.Applicant.firstname} ${app.Applicant.lastname}`,
      ittype: app.vacancy.vacancy_position.name.toUpperCase(),
      status: app.status || "Unknown",
      state: app.Applicant.state,
      age:
        new Date().getFullYear() -
        new Date(app.Applicant.date_of_birth).getFullYear(),
      cos:
        app.ApplicationsAcademicQualification?.course?.name ||
        app.ApplicationNYSCDetails?.course?.name,
      date: new Date(app.created_at).toLocaleDateString(),
      batch: app.vacancy.batch.name,
      companylocation: app.vacancy.location.name,
      businessunit: app.vacancy.business_unit.name,
      original: app,
    }));
  }, [data]);

  const table = useReactTable({
    data: tableData,
    columns,
    pageCount: data?.totalPages || -1,
    state: {
      pagination,
      rowSelection,
    },
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  if (isError)
    return (
      <div className="p-8 text-center text-red-500">
        Error loading applications.
      </div>
    );

  return (
    <>
      <Card className="w-full max-w-[95%] m-auto border-slate-200 shadow-sm bg-white mt-4">
        <CardHeader className="flex flex-col gap-4 p-4 border-b border-slate-100 bg-gray-50/40">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <DateRangeFilter
                dateRange={dateRange}
                setDateRange={setDateRange}
                minDate={data?.meta?.minDate}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs border-dashed border-gray-300"
                  >
                    <Funnel className="mr-2 h-3 w-3" />
                    Type: {selectedItType || "All"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-48 max-h-64 overflow-y-auto"
                >
                  <DropdownMenuItem onClick={() => setSelectedItType(null)}>
                    {!selectedItType && <Check className="mr-2 h-3 w-3" />} All
                    Types
                  </DropdownMenuItem>
                  {uniqueItTypes.map((type) => (
                    <DropdownMenuItem
                      key={type}
                      onClick={() => setSelectedItType(type)}
                    >
                      {selectedItType === type && (
                        <Check className="mr-2 h-3 w-3" />
                      )}{" "}
                      {type}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs border-dashed border-gray-300"
                  >
                    <CalendarBlank className="mr-2 h-3 w-3" />
                    Batch: {selectedBatch || "All"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-48 max-h-64 overflow-y-auto"
                >
                  <DropdownMenuItem onClick={() => setSelectedBatch(null)}>
                    {!selectedBatch && <Check className="mr-2 h-3 w-3" />} All
                    Batches
                  </DropdownMenuItem>
                  {uniqueBatches.map((batch) => (
                    <DropdownMenuItem
                      key={batch}
                      onClick={() => setSelectedBatch(batch)}
                    >
                      {selectedBatch === batch && (
                        <Check className="mr-2 h-3 w-3" />
                      )}{" "}
                      {batch}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs border-dashed border-gray-300"
                  >
                    <MapPin className="mr-2 h-3 w-3" />
                    Loc: {selectedLocation || "All"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-48 max-h-64 overflow-y-auto"
                >
                  <DropdownMenuItem onClick={() => setSelectedLocation(null)}>
                    {!selectedLocation && <Check className="mr-2 h-3 w-3" />}{" "}
                    All Locations
                  </DropdownMenuItem>
                  {uniqueLocations.map((loc) => (
                    <DropdownMenuItem
                      key={loc}
                      onClick={() => setSelectedLocation(loc)}
                    >
                      {selectedLocation === loc && (
                        <Check className="mr-2 h-3 w-3" />
                      )}{" "}
                      {loc}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={`h-8 text-xs border-dashed border-green-500 text-green-700 ${selectedStatus ? "bg-green-50" : ""}`}
                  >
                    <Funnel className="mr-2 h-3 w-3" />
                    Status: {selectedStatus || "Pending"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-48 max-h-64 overflow-y-auto"
                >
                  <DropdownMenuItem onClick={() => setSelectedStatus(null)}>
                    {!selectedStatus && <Check className="mr-2 h-3 w-3" />}{" "}
                    Pending (Default)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSelectedStatus("ACCEPTED")}>
                    {selectedStatus === "ACCEPTED" && <Check className="mr-2 h-3 w-3" />}{" "}
                    Accepted
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSelectedStatus("REJECTED")}>
                    {selectedStatus === "REJECTED" && <Check className="mr-2 h-3 w-3" />}{" "}
                    Rejected
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
              <div className="relative w-full sm:w-64">
                <MagnifyingGlass
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={14}
                />
                <Input
                  placeholder="Search applicants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearch}
                  className="pl-9 h-8 text-xs bg-white border-slate-200 focus:border-green-500 focus:ring-green-500"
                />
              </div>

              <ExportDropdown
                onExport={() => { }}
                data={tableData}
                columns={exportColumns}
                className="h-8"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0 relative">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-green-600 animate-spin" />
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-gray-50/50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="hover:bg-transparent border-b border-slate-100"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className="h-9 text-[11px] font-bold text-gray-500 uppercase tracking-wider"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-64 text-center"
                    >
                      <div className="flex justify-center items-center w-full h-full">
                        <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className={`hover:bg-green-50/30 transition-colors border-b border-slate-100 cursor-pointer group ${row.getIsSelected() ? "bg-green-50/50" : ""
                        }`}
                      onClick={() => handleRowClick(row)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="py-3">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-32 text-center text-gray-500"
                    >
                      No applications found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>

        {hasSelections && (
          <div className="sticky bottom-0 bg-green-50 border-t border-green-200 px-4 py-3 flex items-center justify-center z-50">
            <div className="flex items-center justify-between w-full max-w-7xl">
              <span className="text-sm font-semibold text-green-900">
                {Object.keys(rowSelection).length} application(s) selected
              </span>
              <div className="flex items-center gap-3">
                <Button
                  size="sm"
                  className="h-9 bg-green-600 hover:bg-green-700 text-white text-xs px-4"
                  onClick={() => {
                    setBulkResult(null);
                    bulkAcceptMutation.reset();
                    setIsBulkModalOpen(true);
                  }}
                >
                  <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                  Accept
                </Button>
                <div className="h-6 w-px bg-green-300"></div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-9 text-xs text-green-700 hover:text-green-900 hover:bg-green-100 px-4"
                  onClick={() => setRowSelection({})}
                >
                  <XCircle size={16} className="mr-1" />
                  Clear Selections
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between p-2 px-4 border-t border-slate-100 bg-gray-50/30">
          <div className="text-[11px] text-gray-500">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="h-7 w-20 bg-white text-[11px]"
            >
              <CaretLeft className="mr-1" size={12} /> Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="h-7 w-20 bg-white text-[11px]"
            >
              Next <CaretRight className="ml-1" size={12} />
            </Button>
          </div>
        </div>

        <BulkAcceptModal
          isOpen={isBulkModalOpen}
          onClose={() => setIsBulkModalOpen(false)}
          selectedApplicants={table.getSelectedRowModel().rows.map((r) => r)}
          onConfirm={handleConfirmBulkAccept}
          isPending={bulkAcceptMutation.isPending}
          result={bulkResult}
        />
      </Card>

      <Sheet
        open={!!selectedApplication}
        onOpenChange={(open) => !open && setSelectedApplication(null)}
      >
        <SheetContent side="right" className="sm:max-w-[65%] w-full">
          {selectedApplication && (
            <AcceptanceDetailsViewer
              applicationId={selectedApplication.id}
              internshipType={selectedApplication.type}
              onActionComplete={() => setSelectedApplication(null)}
            />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
